import {
    Briefcase,
    Banknote,
    TrendingUp,
    BarChart,
    Wallet,
    Rocket,
    Globe
} from "lucide-react";

const Hero = {
    subtitle: "Introducing the Future",
    title: "A Trusted Partner for Finance, Research, and Strategic Execution",
    description: "Seternity Solutions is a strategic outsourcing and advisory firm helping businesses and investors execute critical finance and research functions with accuracy, discipline, and scale.",
    ctaButton: "Contact Our Team",
    imageUrl: "/images/about-hero-section.png"
}

const OurStory = {
    title: "Built for Performance. Scaled for Impact.",
    description1: "Seternity Solutions was founded with a clear purpose: to help businesses and institutional investors operate more efficiently by combining domain expertise, structured processes, and global delivery capabilities.",
    description2: "What began as a focused offshore analytics provider has evolved into a multi-service advisory and outsourcing partner supporting accounting, capital markets research, market intelligence, business development, and data analytics. ",
    description3: "Today, we serve private equity firms, investment banks, venture capital funds, research desks, and growth-focused businesses worldwide, thereby delivering disciplined, high-quality outputs that reduce cost, save time, and improve decision-making. ",
    description4: "Our delivery centres in India allow us to provide institutional-grade support at significantly optimized cost structures, without compromising precision, confidentiality, or quality.",
    image: "/images/about-journey.png",
    CEOquote: "We built Seternity to deliver work we would trust at an institutional level."

}

const WhoWeAre = {
    title: "Who We Are",
    visionTitle: "Our Vision.",
    visionDescription: "To become a globally trusted operations and research partner for businesses and institutional investors seeking efficiency, scalability, and measurable performance improvement.",
    missionTitle: "Our Mission.",
    missionDescription: "To deliver high-value finance, research, and analytics solutions with speed, accuracy, and accountability, enabling our clients to focus on strategy while we execute with discipline.",
    image: "/images/about-mission.png",

}

const whoWeServe = {
    title: "Built to Scale. Delivered as a Service. ",
    serve: [
        {
            icon: <Briefcase className="w-8 h-8 text-[#4F7AC2] group-hover:text-white transition-colors duration-300" />,
            title: "Buy-Side Firms",
            description:
                "We support private equity and venture capital teams with research, financial analysis, and portfolio oversight — helping them evaluate opportunities, monitor performance, and manage reporting across their investments."
        },
        {
            icon: <Banknote className="w-8 h-8 text-[#4F7AC2] group-hover:text-white transition-colors duration-300" />,
            title: "Investment Banks",
            description:
                "We work with investment banking teams by providing financial analysis, research support, and execution assistance to help them manage transactions and meet tight deadlines."
        },
        {
            icon: <BarChart className="w-8 h-8 text-[#4F7AC2] group-hover:text-white transition-colors duration-300" />,
            title: "Institutional Research Teams",
            description:
                "We help research teams by delivering accurate data, structured analysis, and timely insights that support investment research and market analysis work. "
        },
        {
            icon: <Wallet className="w-8 h-8 text-[#4F7AC2] group-hover:text-white transition-colors duration-300" />,
            title: "Asset & Wealth Managers",
            description:
                "We support asset and wealth managers with portfolio analysis, performance tracking, and reporting to help them make informed, long-term investment decisions."
        },
        {
            icon: <Rocket className="w-8 h-8 text-[#4F7AC2] group-hover:text-white transition-colors duration-300" />,
            title: "Growth-Stage and Mid-Market Businesses",
            description:
                "We help growing businesses strengthen their finance and analytics functions, improve reporting, and gain better visibility into performance and costs."
        },
        {
            icon: <Globe className="w-8 h-8 text-[#4F7AC2] group-hover:text-white transition-colors duration-300" />,
            title: "Global Corporations",
            description:
                "We support large organizations with finance, analytics, and operational work, helping teams improve efficiency and maintain consistency across regions and business units."
        }
    ]
};

const OurValues = {
    title: "Our Values",
    description: "Our principles define how we operate, how we partner with clients, and how we measure success.",
    button: "Discover Our Values",
    buttonLink: "/contact",
    imageUrl: "/images/about-values.png",
    values: [
        {
            title: "Performance-Driven",
            description:
                "We measure success by the tangible impact we create - improved efficiency, reduced cost, and enhanced output."
        },
        {
            title: "Quality & Precision",
            description:
                "Structured workflows, standardized methodologies, and continuous improvement define our delivery standards. "
        },
        {
            title: "Integrity & Professionalism",
            description:
                "We adhere to the highest ethical and confidentiality standards across global financial and corporate engagements."
        },
        {
            title: "Accountability",
            description:
                "We operate as an extension of our clients’ teams thereby taking ownership of outcomes, not just tasks."
        },
    ]
}


const TeamMemeber = {
    subText: "Leadership & Experts",
    title: "The Intelligent Minds Behind Our Organization",
    description: "Seternity is led by experienced management consultants, financial analysts, technologists, and research professionals with over 100 years of combined leadership experience. Our executive team blends entrepreneurial thinking with disciplined execution — ensuring strategic direction, operational rigor, and long-term client partnerships.",
    Members: [
        {
            name: "Abhishek Mehta",
            role: "Chief Executive Officer",
            bio:
                "Provides strategic leadership and direction across the organization, with a focus on operational performance, growth, and long-term value creation."
        },
        {
            name: "Vikas Mogra",
            role: "Senior Managing Director",
            bio:
                "Oversees client engagements and strategic initiatives, driving execution excellence and alignment with client objectives."
        },
        {
            name: "Swati Tallera",
            role: "Chief Financial Officer",
            bio:
                "Leads financial strategy, governance, and reporting, ensuring fiscal discipline, transparency, and sustainable growth."
        },
        {
            name: "Saurabh Mehta",
            role: "Vice President, IT",
            bio:
                "Responsible for technology strategy, infrastructure, and systems that enable secure, scalable, and efficient operations."
        },
        {
            name: "Ankit Picholiya",
            role: "Vice President, Marketing",
            bio:
                "Drives brand strategy, market positioning, and go-to-market initiatives to support growth and client engagement."
        },
        {
            name: "Ankita Bajaj",
            role: "Vice President, Human Resources",
            bio:
                "Leads talent strategy, organizational development, and culture initiatives to build high-performing teams."
        }
    ]
}

const testimonials = {
    title: " What Clients are Saying about Us",
    description: "Hear what our clients have to say about working with us. We pride ourselves on delivering exceptional service, expert guidance, and measurable results that help businesses grow.",
    clients: [
        {
            id: 1,
            title: "Founding Partner",
            firm: "New York Based Independent Sponsor",
            service: "Private Equity Research",
            text: "Seternity Solutions has been a reliable research partner for our private equity deal evaluation process. Their team consistently delivers thorough, well-structured investment research that aligns with our underwriting standards and timelines. The quality of their sector analysis and transaction support has helped us move faster and make more informed investment decisions.",
        },
        {
            id: 2,
            title: "Managing Partner",
            firm: "NY Based Private Equity Firm",
            service: "Investment and Deal Advisory",
            text: "Working with Seternity Solutions was a seamless experience. Their team demonstrated strong commercial judgment and a practical understanding of deal dynamics in the ES&E sector. They were proactive, detail-oriented, and solution-driven, helping us navigate complexities with clarity and confidence. We value partners who think ahead and execute with discipline, and Seternity consistently delivered on both fronts",
        },

    ]
}

const faqs = {

    title: "Find Answer You Needs",
    description: "From strategic planning to sustainable growth, our tailored solutions help businesses and individuals achieve clarity, security, and long-term success.",
    button: "Get Started",
    buttonLink: "/contact",
    button2: "Our Services",
    button2Link: "/#services",
    image: "/images/faq.jpg",
    faq: [
        {
            question: "What inspired the formation of Seternity Solutions?",
            answer:
                "Seternity was founded to bridge the gap between rising operational demands and limited internal bandwidth within finance, research, and analytics teams. We saw organizations struggling with high costs, fragmented workflows, and inefficient scaling. Seternity was created to provide structured, scalable, and cost-efficient support through a Team-as-a-Service model that enhances output without increasing management burden."
        },
        {
            question: "Who does Seternity Solutions work with? ",
            answer:
                "We partner with private equity firms, investment banks, venture capital funds, institutional research teams, asset managers, and growth-focused businesses. Our clients range from boutique advisory firms to global financial institutions seeking reliable, scalable execution support."
        },
        {
            question: "What services does Seternity Solutions provide?",
            answer:
                "We deliver accounting and bookkeeping, capital markets research, investment research, market research, business development support, and data analytics outsourcing. Our services span the full operational lifecycle, from transaction support and financial modeling to portfolio monitoring and strategic reporting. "
        },
        {
            question: "What values guide Seternity’s approach?",
            answer:
                "Our approach is built on precision, accountability, confidentiality, and measurable performance impact. We prioritize structured processes, disciplined analytics, ethical standards, and long-term partnership over short-term outsourcing transactions."
        },
        {
            question: "Why should organizations choose Seternity as their strategic partner?",
            answer:
                "We combine domain expertise with technology-enabled workflows and global delivery efficiency. Our clients benefit from reduced overhead, faster turnaround times, scalable capacity, and institutional-grade outputs, all delivered with minimal oversight requirements."
        },
        {
            question: "How is Seternity different from a traditional outsourcing firm?",
            answer:
                "Unlike conventional outsourcing providers focused primarily on cost arbitrage, Seternity integrates deeply with client teams, aligns to investment and operational objectives, and delivers decision-ready work products. We emphasize structured execution, sector expertise, and long-term partnership rather than transactional support."
        }
    ]
}

export {
    Hero,
    OurStory,
    whoWeServe,
    WhoWeAre,
    OurValues,
    TeamMemeber,
    testimonials,
    faqs,
}
